import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Alert,
  CircularProgress,
  Chip,
  FormControlLabel,
  Switch,
  Divider,
  Card,
  CardContent,
  Tooltip,
  IconButton,
  Slider,
  InputAdornment,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  PlayArrow as PlayArrowIcon,
  CheckCircle as CheckCircleIcon,
  ArrowBack as ArrowBackIcon,
  InfoOutlined as InfoIcon,
} from '@mui/icons-material';

import {
  getTenantId,
  listDeteriorationModels,
  listCostTables,
  listRiskConfigs,
  listAssetFilters,
  validateRun,
  submitRun,
  DeteriorationModel,
  CostTable,
  RiskConfig,
  AssetFilterOptions,
  RunConfigPayload,
  STRATEGY_OPTIONS,
  STRATEGY_DESCRIPTIONS,
} from '../../services/amsoApi';

const INFO_TOOLTIP_PROPS = {
  tooltip: {
    sx: {
      bgcolor: 'background.paper',
      color: 'text.primary',
      boxShadow: 3,
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 1,
      p: 1.5,
      maxWidth: 320,
      fontSize: '0.8125rem',
      lineHeight: 1.5,
    },
  },
  arrow: {
    sx: {
      color: 'background.paper',
      '&::before': { border: '1px solid', borderColor: 'divider' },
    },
  },
} as const;

const FieldInfo = ({ tip }: { tip: string }) => (
  <InputAdornment position="end">
    <Tooltip
      title={tip}
      arrow
      placement="top"
      componentsProps={INFO_TOOLTIP_PROPS}>
      <IconButton size="small" edge="end" tabIndex={-1}>
        <InfoIcon sx={{ fontSize: '1rem' }} />
      </IconButton>
    </Tooltip>
  </InputAdornment>
);

const OBJECTIVE_OPTIONS = [
  { value: 'risk', label: 'Minimize Risk' },
  { value: 'condition', label: 'Maximize Condition' },
];

const GROUPING_METHOD_OPTIONS = [
  { value: 'auto', label: 'Auto' },
  { value: 'kmeans', label: 'K-Means' },
  { value: 'spatial', label: 'Spatial' },
];

const COST_MODEL_OPTIONS = [
  { value: 'per_foot', label: 'Per Foot' },
  { value: 'per_unit', label: 'Per Unit' },
];

interface StrategyFormValues {
  unit_cost_per_foot: number;
  condition_improvement: number;
  cost_escalation_rate: number;
  disruption_days: number;
  spatial_discount_max: number;
}

interface FormValues {
  name: string;
  deterioration_model_id: string;
  cost_table_id: string;
  risk_config_id: string;
  materials: string[];
  planning_horizon: number;
  annual_budget: number;
  tier: number;
  primary_objective: string;

  n_pareto_points: number;
  population_size: number;
  n_generations: number;
  failure_penalty: number;
  max_network_disruption_pct: number | null;
  early_stop_patience: number;

  condition_failure: number;
  condition_critical: number;
  condition_poor: number;

  strategies: {
    do_nothing: StrategyFormValues;
    spot_repair: StrategyFormValues;
    cipp_section_lining: StrategyFormValues;
    replace: StrategyFormValues;
  };

  grouping_method: string;
  spatial_weight: number;
  max_radius_km: number;
  min_group_size: number;
  max_group_size: number;
  compact_threshold_km: number;

  spatial_compact_threshold_km: number;
  cost_model: string;

  solver_time_limit: number;
  mip_rel_gap: number;

  inspection_enabled: boolean;
  inspection_min_structural_grade: number;
  inspection_min_operational_grade: number;
  trench_length_per_point_defect_ft: number;
  trench_buffer_ft: number;
  trench_merge_gap_ft: number;
  freshness_full_confidence_years: number;
  freshness_reduced_confidence_years: number;
  freshness_low_confidence_years: number;
  stale_data_condition_gap: number;
  point_repair_max_fraction: number;
  section_lining_max_fraction: number;
  rehabilitation_max_fraction: number;
  repair_to_replace_threshold: number;

  cof_estimation_enabled: boolean;
  cof_gis_weight: number;
  cof_esri_buffer_meters: number;
  cof_esri_hospitals_weight: number;
  cof_esri_schools_weight: number;
  cof_esri_fire_stations_weight: number;
  cof_proxy_diameter: number;
  cof_proxy_material: number;
  cof_proxy_subtype: number;
  cof_proxy_length: number;
  cof_critical_assets: string;
  default_consequence: number;
}

const DEFAULT_VALUES: FormValues = {
  name: '',
  deterioration_model_id: '',
  cost_table_id: '',
  risk_config_id: '',
  materials: [],
  planning_horizon: 5,
  annual_budget: 5_000_000,
  tier: 1,
  primary_objective: 'risk',

  n_pareto_points: 25,
  population_size: 150,
  n_generations: 500,
  failure_penalty: 100,
  max_network_disruption_pct: 50,
  early_stop_patience: 50,

  condition_failure: 1.0,
  condition_critical: 20.0,
  condition_poor: 40.0,

  strategies: {
    do_nothing: {
      unit_cost_per_foot: 0,
      condition_improvement: 0,
      cost_escalation_rate: 0,
      disruption_days: 0,
      spatial_discount_max: 0,
    },
    spot_repair: {
      unit_cost_per_foot: 75,
      condition_improvement: 15,
      cost_escalation_rate: 0.025,
      disruption_days: 5,
      spatial_discount_max: 0.15,
    },
    cipp_section_lining: {
      unit_cost_per_foot: 85,
      condition_improvement: 35,
      cost_escalation_rate: 0.02,
      disruption_days: 10,
      spatial_discount_max: 0.12,
    },
    replace: {
      unit_cost_per_foot: 250,
      condition_improvement: 100,
      cost_escalation_rate: 0.03,
      disruption_days: 20,
      spatial_discount_max: 0.2,
    },
  },

  grouping_method: 'auto',
  spatial_weight: 0.7,
  max_radius_km: 2.0,
  min_group_size: 2,
  max_group_size: 50,
  compact_threshold_km: 0.5,

  spatial_compact_threshold_km: 1.0,
  cost_model: 'per_foot',

  solver_time_limit: 3600,
  mip_rel_gap: 0.005,

  inspection_enabled: true,
  inspection_min_structural_grade: 2,
  inspection_min_operational_grade: 3,
  trench_length_per_point_defect_ft: 15.0,
  trench_buffer_ft: 7.5,
  trench_merge_gap_ft: 7.5,
  freshness_full_confidence_years: 3.0,
  freshness_reduced_confidence_years: 7.0,
  freshness_low_confidence_years: 15.0,
  stale_data_condition_gap: 30.0,
  point_repair_max_fraction: 0.15,
  section_lining_max_fraction: 0.4,
  rehabilitation_max_fraction: 0.8,
  repair_to_replace_threshold: 0.8,

  cof_estimation_enabled: true,
  cof_gis_weight: 0.4,
  cof_esri_buffer_meters: 500,
  cof_esri_hospitals_weight: 3.0,
  cof_esri_schools_weight: 2.0,
  cof_esri_fire_stations_weight: 1.5,
  cof_proxy_diameter: 0.35,
  cof_proxy_material: 0.25,
  cof_proxy_subtype: 0.25,
  cof_proxy_length: 0.15,
  cof_critical_assets: '',
  default_consequence: 5.0,
};

function formatBudget(value: number): string {
  return value.toLocaleString('en-US');
}

function parseBudget(formatted: string): number {
  const raw = formatted.replace(/,/g, '');
  const n = Number(raw);
  return Number.isNaN(n) ? 0 : n;
}

const STRATEGY_LABELS: Record<string, string> = {
  do_nothing: 'Do Nothing',
  spot_repair: 'Spot Repair',
  cipp_section_lining: 'CIPP Section Lining',
  replace: 'Full Replacement',
};

function RunConfigPage() {
  const history = useHistory();
  const tenantId = getTenantId();

  const [form, setForm] = useState<FormValues>(DEFAULT_VALUES);
  const [models, setModels] = useState<DeteriorationModel[]>([]);
  const [costTables, setCostTables] = useState<CostTable[]>([]);
  const [riskConfigs, setRiskConfigs] = useState<RiskConfig[]>([]);
  const [filterOptions, setFilterOptions] = useState<AssetFilterOptions | null>(
    null,
  );

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [validationResult, setValidationResult] = useState<{
    valid: boolean;
    warnings: string[];
    errors: string[];
  } | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (!tenantId) {
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const [m, ct, rc, af] = await Promise.all([
          listDeteriorationModels(tenantId),
          listCostTables(tenantId),
          listRiskConfigs(tenantId),
          listAssetFilters(tenantId),
        ]);
        setModels(m);
        setCostTables(ct);
        setRiskConfigs(rc);
        setFilterOptions(af);
      } catch (err) {
        console.error('Failed to load discovery data', err);
      } finally {
        setLoading(false);
      }
    })();
  }, [tenantId]);

  const selectedModel = models.find(m => m.id === form.deterioration_model_id);

  const updateField = <K extends keyof FormValues>(
    key: K,
    value: FormValues[K],
  ) => {
    setForm(prev => ({ ...prev, [key]: value }));
    setValidationResult(null);
    setSubmitError(null);
  };

  const handleModelChange = (modelId: string) => {
    setForm(prev => ({
      ...prev,
      deterioration_model_id: modelId,
      materials: [],
    }));
    setValidationResult(null);
    setSubmitError(null);
  };

  const updateStrategy = (stratKey: string, field: string, value: number) => {
    setForm(prev => ({
      ...prev,
      strategies: {
        ...prev.strategies,
        [stratKey]: {
          ...prev.strategies[stratKey as keyof typeof prev.strategies],
          [field]: value,
        },
      },
    }));
  };

  const buildPayload = (): RunConfigPayload => {
    const payload: RunConfigPayload = {
      tenant_id: tenantId || '',
      name: form.name.trim() || undefined,
      ...(form.deterioration_model_id
        ? { deterioration_model_id: form.deterioration_model_id }
        : {}),
      planning_horizon: form.planning_horizon,
      annual_budget: form.annual_budget,
      tier: form.tier,
      primary_objective: form.primary_objective,

      n_pareto_points: form.n_pareto_points,
      population_size: form.population_size,
      n_generations: form.n_generations,
      ...(form.primary_objective === 'risk' && form.tier !== 3
        ? { failure_penalty: form.failure_penalty }
        : {}),
      ...(form.max_network_disruption_pct !== null
        ? { max_network_disruption_pct: form.max_network_disruption_pct / 100 }
        : {}),
      early_stop_patience: form.early_stop_patience,

      condition_failure: form.condition_failure,
      condition_critical: form.condition_critical,
      condition_poor: form.condition_poor,

      strategies: form.strategies,

      grouping_method: form.grouping_method,
      spatial_weight: form.spatial_weight,
      max_radius_km: form.max_radius_km,
      min_group_size: form.min_group_size,
      max_group_size: form.max_group_size,
      compact_threshold_km: form.compact_threshold_km,

      spatial_compact_threshold_km: form.spatial_compact_threshold_km,
      cost_model: form.cost_model,

      solver_time_limit: form.solver_time_limit,
      mip_rel_gap: form.mip_rel_gap,

      inspection_enabled: form.inspection_enabled,
      inspection_min_structural_grade: form.inspection_min_structural_grade,
      inspection_min_operational_grade: form.inspection_min_operational_grade,
      trench_length_per_point_defect_ft: form.trench_length_per_point_defect_ft,
      trench_buffer_ft: form.trench_buffer_ft,
      trench_merge_gap_ft: form.trench_merge_gap_ft,
      freshness_full_confidence_years: form.freshness_full_confidence_years,
      freshness_reduced_confidence_years:
        form.freshness_reduced_confidence_years,
      freshness_low_confidence_years: form.freshness_low_confidence_years,
      stale_data_condition_gap: form.stale_data_condition_gap,
      intervention_thresholds: {
        point_repair_max_fraction: form.point_repair_max_fraction,
        section_lining_max_fraction: form.section_lining_max_fraction,
        rehabilitation_max_fraction: form.rehabilitation_max_fraction,
      },
      repair_to_replace_threshold: form.repair_to_replace_threshold,

      cof_estimation_enabled: form.cof_estimation_enabled,
      cof_gis_weight: form.cof_gis_weight,
      cof_esri_config: {
        buffer_meters: form.cof_esri_buffer_meters,
        facility_layers: {
          hospitals: { weight: form.cof_esri_hospitals_weight },
          schools: { weight: form.cof_esri_schools_weight },
          fire_stations: { weight: form.cof_esri_fire_stations_weight },
        },
      },
      cof_proxy_weights: {
        diameter: form.cof_proxy_diameter,
        material: form.cof_proxy_material,
        subtype: form.cof_proxy_subtype,
        length: form.cof_proxy_length,
      },
      cof_critical_assets: form.cof_critical_assets
        .split(',')
        .map(s => s.trim())
        .filter(Boolean),
      default_consequence: form.default_consequence,
    };

    if (form.cost_table_id) payload.cost_table_id = form.cost_table_id;
    if (form.risk_config_id) payload.risk_config_id = form.risk_config_id;

    const sysType = selectedModel?.system_type;
    const astType = selectedModel?.asset_type;
    if (sysType || astType || form.materials.length > 0) {
      payload.asset_filters = {};
      if (sysType) payload.asset_filters.system_type = sysType;
      if (astType) payload.asset_filters.asset_type = astType;
      if (form.materials.length > 0)
        payload.asset_filters.materials = form.materials;
    }

    return payload;
  };

  const handleValidate = async () => {
    try {
      const result = await validateRun(buildPayload());
      setValidationResult(result);
    } catch (err: any) {
      setValidationResult({
        valid: false,
        warnings: [],
        errors: [err.message],
      });
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const result = await submitRun(buildPayload());
      history.push(`/amso/runs/${result.run_id}`);
    } catch (err: any) {
      setSubmitError(err.response?.data?.detail || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (!tenantId) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="warning">
          No tenant ID found. Please log in through Info360 first.
        </Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ p: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
        <CircularProgress size={24} />
        <Typography>Loading configuration options...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 900, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => history.push('/amso')}
          size="small">
          Back
        </Button>
        <Typography variant="h5">New Optimization Run</Typography>
      </Box>

      <TextField
        fullWidth
        label="Run Name"
        placeholder="e.g. Downtown Sewer 2026 Q2"
        value={form.name}
        onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
        helperText="Give this run a descriptive name (optional)"
        sx={{ mb: 3 }}
      />

      {/* Basic Configuration */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Basic Configuration
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Deterioration Model</InputLabel>
                <Select
                  value={form.deterioration_model_id}
                  label="Deterioration Model"
                  onChange={e => handleModelChange(e.target.value as string)}>
                  <MenuItem value="">
                    <em>None (use industry standard curves)</em>
                  </MenuItem>
                  {models.map(m => (
                    <MenuItem key={m.id} value={m.id}>
                      {m.name}
                      {m.system_type && (
                        <Chip
                          label={m.system_type}
                          size="small"
                          sx={{ ml: 1 }}
                        />
                      )}
                      {m.asset_type && (
                        <Chip
                          label={m.asset_type}
                          size="small"
                          sx={{ ml: 0.5 }}
                          variant="outlined"
                        />
                      )}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {selectedModel &&
                (selectedModel.system_type || selectedModel.asset_type) && (
                  <Box
                    sx={{
                      mt: 1,
                      display: 'flex',
                      gap: 1,
                      alignItems: 'center',
                    }}>
                    <Typography variant="caption" color="text.secondary">
                      Scope:
                    </Typography>
                    {selectedModel.system_type && (
                      <Chip label={selectedModel.system_type} size="small" />
                    )}
                    {selectedModel.asset_type && (
                      <Chip
                        label={selectedModel.asset_type}
                        size="small"
                        variant="outlined"
                      />
                    )}
                  </Box>
                )}
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Cost Table (optional)</InputLabel>
                <Select
                  value={form.cost_table_id}
                  label="Cost Table (optional)"
                  onChange={e =>
                    updateField('cost_table_id', e.target.value as string)
                  }>
                  <MenuItem value="">None</MenuItem>
                  {costTables.map(ct => (
                    <MenuItem key={ct.id} value={ct.id}>
                      {ct.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Risk Configuration (optional)</InputLabel>
                <Select
                  value={form.risk_config_id}
                  label="Risk Configuration (optional)"
                  onChange={e =>
                    updateField('risk_config_id', e.target.value as string)
                  }>
                  <MenuItem value="">None</MenuItem>
                  {riskConfigs.map(rc => (
                    <MenuItem key={rc.id} value={rc.config_id}>
                      {rc.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Optimization Strategy</InputLabel>
                <Select
                  value={form.tier}
                  label="Optimization Strategy"
                  onChange={e => updateField('tier', Number(e.target.value))}>
                  {STRATEGY_OPTIONS.map(t => (
                    <MenuItem key={t.value} value={t.value}>
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {t.label}
                        <Tooltip
                          title={STRATEGY_DESCRIPTIONS[t.value]}
                          arrow
                          componentsProps={INFO_TOOLTIP_PROPS}>
                          <InfoIcon
                            fontSize="small"
                            sx={{ color: 'text.disabled', fontSize: '1rem' }}
                          />
                        </Tooltip>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Primary Objective</InputLabel>
                <Select
                  value={form.primary_objective}
                  label="Primary Objective"
                  onChange={e =>
                    updateField('primary_objective', e.target.value as string)
                  }>
                  {OBJECTIVE_OPTIONS.map(o => (
                    <MenuItem key={o.value} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Annual Budget ($)"
                value={formatBudget(form.annual_budget)}
                onChange={e =>
                  updateField('annual_budget', parseBudget(e.target.value))
                }
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Planning Horizon (years)"
                type="number"
                value={form.planning_horizon}
                onChange={e =>
                  updateField('planning_horizon', Number(e.target.value))
                }
                inputProps={{ min: 1, max: 50 }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Advanced Configuration */}
      <Accordion sx={{ mb: 3 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Advanced Configuration</Typography>
        </AccordionSummary>
        <AccordionDetails>
          {/* Filters */}
          {filterOptions && filterOptions.materials.length > 0 && (
            <>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Filters
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Materials (optional)</InputLabel>
                    <Select
                      multiple
                      value={form.materials}
                      label="Materials (optional)"
                      onChange={e =>
                        updateField('materials', e.target.value as string[])
                      }
                      renderValue={selected =>
                        (selected as string[]).join(', ')
                      }>
                      {filterOptions.materials.map(m => (
                        <MenuItem key={m} value={m}>
                          {m}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </>
          )}

          {/* Optimization */}
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            Optimization
          </Typography>
          {form.primary_objective === 'risk' && form.tier !== 3 && (
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Typography variant="body2">
                  Failure Tolerance: <strong>{form.failure_penalty}</strong>
                </Typography>
                <Tooltip
                  title="Controls the trade-off between pure risk minimization and preventing asset failures. At low values, the optimizer focuses entirely on reducing risk-weighted outcomes, even if some low-consequence assets are allowed to fail. At high values, it actively prevents any asset from reaching failure condition, which may come at the cost of slightly higher overall risk."
                  arrow
                  componentsProps={INFO_TOOLTIP_PROPS}>
                  <IconButton size="small" sx={{ ml: 0.5 }}>
                    <InfoIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              <Box sx={{ px: 1 }}>
                <Slider
                  value={form.failure_penalty}
                  onChange={(_, v) =>
                    updateField('failure_penalty', v as number)
                  }
                  min={1}
                  max={200}
                  valueLabelDisplay="auto"
                />
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  mt: -0.5,
                }}>
                <Typography variant="caption" color="text.secondary">
                  Allow failures
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Balanced
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Prevent failures
                </Typography>
              </Box>
            </Box>
          )}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="Max Network Disruption %"
                type="number"
                value={form.max_network_disruption_pct ?? ''}
                onChange={e =>
                  updateField(
                    'max_network_disruption_pct',
                    e.target.value === '' ? null : Number(e.target.value),
                  )
                }
                inputProps={{ min: 1, max: 100, step: 1 }}
                helperText="1–100, leave empty to disable"
                InputProps={{
                  endAdornment: (
                    <FieldInfo tip="Maximum percentage of asset groups that can be under construction in any single year. Limits simultaneous interventions to manage operational disruption. Applied to all three optimization strategies." />
                  ),
                }}
              />
            </Grid>
            {form.tier === 1 && (
              <>
                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    label="Solver Time Limit (seconds)"
                    type="number"
                    value={form.solver_time_limit}
                    onChange={e =>
                      updateField('solver_time_limit', Number(e.target.value))
                    }
                    InputProps={{
                      endAdornment: (
                        <FieldInfo tip="Maximum time the MILP solver is allowed to run. Longer limits may find better solutions for large problems. The solver will return the best solution found if it runs out of time." />
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    label="MIP Relative Gap"
                    type="number"
                    value={form.mip_rel_gap}
                    onChange={e =>
                      updateField('mip_rel_gap', Number(e.target.value))
                    }
                    inputProps={{ step: 0.001 }}
                    InputProps={{
                      endAdornment: (
                        <FieldInfo tip="Optimality tolerance for the MILP solver. A gap of 0.005 means the solver stops when the solution is proven within 0.5% of optimal. Lower values yield better solutions but take longer." />
                      ),
                    }}
                  />
                </Grid>
              </>
            )}
            {form.tier === 2 && (
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  label="Pareto Points"
                  type="number"
                  value={form.n_pareto_points}
                  onChange={e =>
                    updateField('n_pareto_points', Number(e.target.value))
                  }
                  InputProps={{
                    endAdornment: (
                      <FieldInfo tip="Number of budget levels to evaluate when building the cost-vs-risk trade-off curve. More points give a smoother Pareto front but take proportionally longer to solve." />
                    ),
                  }}
                />
              </Grid>
            )}
            {form.tier === 3 && (
              <>
                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    label="Population Size"
                    type="number"
                    value={form.population_size}
                    onChange={e =>
                      updateField('population_size', Number(e.target.value))
                    }
                    InputProps={{
                      endAdornment: (
                        <FieldInfo tip="Number of candidate solutions maintained per generation in the genetic algorithm (NSGA-II). Larger populations explore more of the solution space but increase runtime per generation." />
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    label="Generations"
                    type="number"
                    value={form.n_generations}
                    onChange={e =>
                      updateField('n_generations', Number(e.target.value))
                    }
                    InputProps={{
                      endAdornment: (
                        <FieldInfo tip="Maximum number of evolutionary iterations. More generations allow the algorithm to refine solutions further but increase total runtime. Early stopping may halt before this limit if the front stagnates." />
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    label="Early Stop Patience"
                    type="number"
                    value={form.early_stop_patience}
                    onChange={e =>
                      updateField('early_stop_patience', Number(e.target.value))
                    }
                    InputProps={{
                      endAdornment: (
                        <FieldInfo tip="Number of consecutive generations without improvement before the optimizer stops early. Lower values save time on easy problems; higher values let the algorithm push through plateaus." />
                      ),
                    }}
                  />
                </Grid>
              </>
            )}
          </Grid>

          {/* Thresholds */}
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            Condition Thresholds
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="Failure Threshold"
                type="number"
                value={form.condition_failure}
                onChange={e =>
                  updateField('condition_failure', Number(e.target.value))
                }
                inputProps={{ min: 0, max: 100, step: 0.5 }}
                InputProps={{
                  endAdornment: (
                    <FieldInfo tip="Condition score (0–100) below which an asset is considered failed. Assets reaching this level trigger slack penalties in the optimizer and are flagged in results." />
                  ),
                }}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="Critical Threshold"
                type="number"
                value={form.condition_critical}
                onChange={e =>
                  updateField('condition_critical', Number(e.target.value))
                }
                inputProps={{ min: 0, max: 100, step: 0.5 }}
                InputProps={{
                  endAdornment: (
                    <FieldInfo tip="Condition score (0–100) below which an asset is in critical condition. Used for reporting and color-coding in the results dashboard." />
                  ),
                }}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="Poor Threshold"
                type="number"
                value={form.condition_poor}
                onChange={e =>
                  updateField('condition_poor', Number(e.target.value))
                }
                inputProps={{ min: 0, max: 100, step: 0.5 }}
                InputProps={{
                  endAdornment: (
                    <FieldInfo tip="Condition score (0–100) below which an asset is in poor condition. Used for reporting and color-coding in the results dashboard." />
                  ),
                }}
              />
            </Grid>
          </Grid>

          {/* Strategies */}
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            Strategies
          </Typography>
          {form.cost_table_id ? (
            <Alert severity="info" sx={{ mb: 1 }}>
              A Cost Table is selected — its per-asset-group unit costs will
              take precedence over the Cost/ft values below. Other strategy
              parameters (condition improvement, disruption, etc.) still apply.
            </Alert>
          ) : (
            <Alert severity="info" sx={{ mb: 1 }}>
              No Cost Table selected — the Cost/ft values below will be used for
              all assets.
            </Alert>
          )}
          {(
            Object.keys(STRATEGY_LABELS) as Array<keyof typeof STRATEGY_LABELS>
          ).map(stratKey => (
            <Box key={stratKey} sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                {STRATEGY_LABELS[stratKey]}
              </Typography>
              <Grid container spacing={1}>
                <Grid item xs={2.4}>
                  <TextField
                    size="small"
                    fullWidth
                    label="Cost/ft ($)"
                    type="number"
                    value={
                      form.strategies[stratKey as keyof typeof form.strategies]
                        .unit_cost_per_foot
                    }
                    onChange={e =>
                      updateStrategy(
                        stratKey,
                        'unit_cost_per_foot',
                        Number(e.target.value),
                      )
                    }
                    InputProps={{
                      endAdornment: (
                        <FieldInfo tip="Unit cost per linear foot for this intervention strategy. Multiplied by each asset's pipe length to compute total cost. Overridden by Cost Table if one is selected." />
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={2.4}>
                  <TextField
                    size="small"
                    fullWidth
                    label="Improvement"
                    type="number"
                    value={
                      form.strategies[stratKey as keyof typeof form.strategies]
                        .condition_improvement
                    }
                    onChange={e =>
                      updateStrategy(
                        stratKey,
                        'condition_improvement',
                        Number(e.target.value),
                      )
                    }
                    InputProps={{
                      endAdornment: (
                        <FieldInfo tip="Condition score points gained when this strategy is applied to an asset group (0–100 scale). Higher values represent more restorative interventions." />
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={2.4}>
                  <TextField
                    size="small"
                    fullWidth
                    label="Escalation Rate"
                    type="number"
                    value={
                      form.strategies[stratKey as keyof typeof form.strategies]
                        .cost_escalation_rate
                    }
                    onChange={e =>
                      updateStrategy(
                        stratKey,
                        'cost_escalation_rate',
                        Number(e.target.value),
                      )
                    }
                    inputProps={{ step: 0.005 }}
                    InputProps={{
                      endAdornment: (
                        <FieldInfo tip="Annual cost escalation rate for this strategy. A rate of 0.02 means costs increase 2% per year. Applied compounding from year 1 onward." />
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={2.4}>
                  <TextField
                    size="small"
                    fullWidth
                    label="Disruption (days)"
                    type="number"
                    value={
                      form.strategies[stratKey as keyof typeof form.strategies]
                        .disruption_days
                    }
                    onChange={e =>
                      updateStrategy(
                        stratKey,
                        'disruption_days',
                        Number(e.target.value),
                      )
                    }
                    InputProps={{
                      endAdornment: (
                        <FieldInfo tip="Estimated construction disruption duration in days. Used for reporting and scheduling, not directly in the optimization objective." />
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={2.4}>
                  <TextField
                    size="small"
                    fullWidth
                    label="Spatial Disc."
                    type="number"
                    value={
                      form.strategies[stratKey as keyof typeof form.strategies]
                        .spatial_discount_max
                    }
                    onChange={e =>
                      updateStrategy(
                        stratKey,
                        'spatial_discount_max',
                        Number(e.target.value),
                      )
                    }
                    inputProps={{ step: 0.05 }}
                    InputProps={{
                      endAdornment: (
                        <FieldInfo tip="Maximum cost discount (0–1) applied to geographically compact groups. A value of 0.15 means up to 15% cost reduction when assets are clustered close together, reflecting construction efficiency gains." />
                      ),
                    }}
                  />
                </Grid>
              </Grid>
            </Box>
          ))}

          {/* Grouping */}
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 600, mb: 1, mt: 2 }}>
            Grouping
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={4}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <FormControl sx={{ flex: 1 }}>
                  <InputLabel>Method</InputLabel>
                  <Select
                    value={form.grouping_method}
                    label="Method"
                    onChange={e =>
                      updateField('grouping_method', e.target.value as string)
                    }>
                    {GROUPING_METHOD_OPTIONS.map(o => (
                      <MenuItem key={o.value} value={o.value}>
                        {o.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Tooltip
                  title="Algorithm used to cluster assets into groups for optimization. Auto selects the best method based on data characteristics. K-Means uses attribute similarity; Spatial prioritizes geographic proximity."
                  arrow
                  placement="top"
                  componentsProps={INFO_TOOLTIP_PROPS}>
                  <IconButton size="small" tabIndex={-1} sx={{ ml: 0.5 }}>
                    <InfoIcon sx={{ fontSize: '1rem' }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="Spatial Weight"
                type="number"
                value={form.spatial_weight}
                onChange={e =>
                  updateField('spatial_weight', Number(e.target.value))
                }
                inputProps={{ min: 0, max: 1, step: 0.1 }}
                InputProps={{
                  endAdornment: (
                    <FieldInfo tip="How much geographic proximity influences grouping (0 = ignore location, 1 = only use location). Higher values create more geographically compact groups that may benefit from spatial cost discounts." />
                  ),
                }}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="Max Radius (km)"
                type="number"
                value={form.max_radius_km}
                onChange={e =>
                  updateField('max_radius_km', Number(e.target.value))
                }
                InputProps={{
                  endAdornment: (
                    <FieldInfo tip="Maximum geographic radius for any single asset group. Groups exceeding this radius are split to keep interventions logistically manageable." />
                  ),
                }}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="Min Group Size"
                type="number"
                value={form.min_group_size}
                onChange={e =>
                  updateField('min_group_size', Number(e.target.value))
                }
                InputProps={{
                  endAdornment: (
                    <FieldInfo tip="Minimum number of assets per group. Very small groups are merged with neighbors to avoid overly granular optimization decisions." />
                  ),
                }}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="Max Group Size"
                type="number"
                value={form.max_group_size}
                onChange={e =>
                  updateField('max_group_size', Number(e.target.value))
                }
                InputProps={{
                  endAdornment: (
                    <FieldInfo tip="Maximum number of assets per group. Large groups are split to maintain decision granularity. Fewer, larger groups speed up the optimizer but reduce precision." />
                  ),
                }}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="Compact Threshold (km)"
                type="number"
                value={form.compact_threshold_km}
                onChange={e =>
                  updateField('compact_threshold_km', Number(e.target.value))
                }
                InputProps={{
                  endAdornment: (
                    <FieldInfo tip="Average pairwise distance below which a group is considered geographically compact. Compact groups are flagged in grouping results and may receive spatial cost discounts." />
                  ),
                }}
              />
            </Grid>
          </Grid>

          {/* Spatial */}
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            Spatial
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <FormControl sx={{ flex: 1 }}>
                  <InputLabel>Cost Model</InputLabel>
                  <Select
                    value={form.cost_model}
                    label="Cost Model"
                    onChange={e =>
                      updateField('cost_model', e.target.value as string)
                    }>
                    {COST_MODEL_OPTIONS.map(o => (
                      <MenuItem key={o.value} value={o.value}>
                        {o.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Tooltip
                  title="How intervention costs are calculated. Per Foot multiplies the strategy's unit cost by each asset's pipe length. Per Unit applies a flat cost per asset regardless of length."
                  arrow
                  placement="top"
                  componentsProps={INFO_TOOLTIP_PROPS}>
                  <IconButton size="small" tabIndex={-1} sx={{ ml: 0.5 }}>
                    <InfoIcon sx={{ fontSize: '1rem' }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Compact Threshold (km)"
                type="number"
                value={form.spatial_compact_threshold_km}
                onChange={e =>
                  updateField(
                    'spatial_compact_threshold_km',
                    Number(e.target.value),
                  )
                }
                inputProps={{ step: 0.1 }}
                InputProps={{
                  endAdornment: (
                    <FieldInfo tip="Distance threshold for the spatial cost discount applied during optimization. Groups with average pairwise distance below this value receive reduced intervention costs due to construction proximity efficiencies." />
                  ),
                }}
              />
            </Grid>
          </Grid>

          {/* Inspection Analysis */}
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 600, mb: 1, mt: 2 }}>
            Inspection Analysis (CCTV)
          </Typography>
          <Box sx={{ mb: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={form.inspection_enabled}
                  onChange={e =>
                    updateField('inspection_enabled', e.target.checked)
                  }
                />
              }
              label="Enable CCTV Inspection Analysis"
            />
            <Tooltip
              title="When enabled, AMSO fetches CCTV inspection data from Info360's inspection-management-service and uses defect-level detail to calculate repair scope, intervention need, and cost. When disabled, all pipes use condition-based estimation."
              arrow
              componentsProps={INFO_TOOLTIP_PROPS}>
              <IconButton size="small" tabIndex={-1}>
                <InfoIcon sx={{ fontSize: '1rem' }} />
              </IconButton>
            </Tooltip>
          </Box>

          {form.inspection_enabled && (
            <>
              <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                Defect Filtering
              </Typography>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Min Structural Grade"
                    type="number"
                    value={form.inspection_min_structural_grade}
                    onChange={e =>
                      updateField(
                        'inspection_min_structural_grade',
                        Number(e.target.value),
                      )
                    }
                    inputProps={{ min: 1, max: 5 }}
                    InputProps={{
                      endAdornment: (
                        <FieldInfo tip="Minimum NASSCO PACP severity grade (1–5) for structural defects to be considered actionable. Grade 1 is minor, grade 5 is collapse. Defects below this threshold are ignored in repair scope calculations." />
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Min Operational Grade"
                    type="number"
                    value={form.inspection_min_operational_grade}
                    onChange={e =>
                      updateField(
                        'inspection_min_operational_grade',
                        Number(e.target.value),
                      )
                    }
                    inputProps={{ min: 1, max: 5 }}
                    InputProps={{
                      endAdornment: (
                        <FieldInfo tip="Minimum NASSCO PACP severity grade (1–5) for operational defects (roots, deposits, infiltration) to be considered actionable. Operational defects are generally less critical than structural ones, so a higher threshold is typical." />
                      ),
                    }}
                  />
                </Grid>
              </Grid>

              <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                Trench Optimization
              </Typography>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    label="Point Defect Trench (ft)"
                    type="number"
                    value={form.trench_length_per_point_defect_ft}
                    onChange={e =>
                      updateField(
                        'trench_length_per_point_defect_ft',
                        Number(e.target.value),
                      )
                    }
                    inputProps={{ min: 1, step: 1 }}
                    InputProps={{
                      endAdornment: (
                        <FieldInfo tip="Length of excavation trench assigned to each point defect (e.g. a crack or break). The trench is centered on the defect location. Larger values account for equipment access and working space." />
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    label="Trench Buffer (ft)"
                    type="number"
                    value={form.trench_buffer_ft}
                    onChange={e =>
                      updateField('trench_buffer_ft', Number(e.target.value))
                    }
                    inputProps={{ min: 0, step: 0.5 }}
                    InputProps={{
                      endAdornment: (
                        <FieldInfo tip="Extra length added to each end of a continuous defect's trench segment. Accounts for equipment footprint and safe working margins beyond the defect boundaries." />
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    label="Merge Gap (ft)"
                    type="number"
                    value={form.trench_merge_gap_ft}
                    onChange={e =>
                      updateField('trench_merge_gap_ft', Number(e.target.value))
                    }
                    inputProps={{ min: 0, step: 0.5 }}
                    InputProps={{
                      endAdornment: (
                        <FieldInfo tip="Maximum gap between two adjacent trench segments before they are merged into a single continuous excavation. Smaller values keep trenches separate; larger values consolidate nearby repairs to reduce mobilization costs." />
                      ),
                    }}
                  />
                </Grid>
              </Grid>

              <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                Data Freshness
              </Typography>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={3}>
                  <TextField
                    fullWidth
                    label="Full Confidence (yrs)"
                    type="number"
                    value={form.freshness_full_confidence_years}
                    onChange={e =>
                      updateField(
                        'freshness_full_confidence_years',
                        Number(e.target.value),
                      )
                    }
                    inputProps={{ min: 0, step: 0.5 }}
                    InputProps={{
                      endAdornment: (
                        <FieldInfo tip="Inspections younger than this age receive full confidence (1.0). CCTV data within this window is considered highly reliable for defect analysis." />
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    fullWidth
                    label="Reduced Confidence (yrs)"
                    type="number"
                    value={form.freshness_reduced_confidence_years}
                    onChange={e =>
                      updateField(
                        'freshness_reduced_confidence_years',
                        Number(e.target.value),
                      )
                    }
                    inputProps={{ min: 0, step: 0.5 }}
                    InputProps={{
                      endAdornment: (
                        <FieldInfo tip="Inspections between full and reduced age thresholds receive reduced confidence (0.85). Condition may have worsened since the inspection but defect locations are still informative." />
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    fullWidth
                    label="Low Confidence (yrs)"
                    type="number"
                    value={form.freshness_low_confidence_years}
                    onChange={e =>
                      updateField(
                        'freshness_low_confidence_years',
                        Number(e.target.value),
                      )
                    }
                    inputProps={{ min: 0, step: 0.5 }}
                    InputProps={{
                      endAdornment: (
                        <FieldInfo tip="Inspections between reduced and low age thresholds receive low confidence (0.6). Beyond this age, inspections get minimal confidence (0.3) and are considered largely stale." />
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    fullWidth
                    label="Stale Condition Gap"
                    type="number"
                    value={form.stale_data_condition_gap}
                    onChange={e =>
                      updateField(
                        'stale_data_condition_gap',
                        Number(e.target.value),
                      )
                    }
                    inputProps={{ min: 0, step: 5 }}
                    InputProps={{
                      endAdornment: (
                        <FieldInfo tip="If the deterioration model's current condition score exceeds the CCTV-implied condition by more than this gap, the pipe is flagged as likely repaired since inspection. Falls back to condition-based estimation for such pipes." />
                      ),
                    }}
                  />
                </Grid>
              </Grid>

              <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                Intervention Thresholds
              </Typography>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={3}>
                  <TextField
                    fullWidth
                    label="Point Repair Max"
                    type="number"
                    value={form.point_repair_max_fraction}
                    onChange={e =>
                      updateField(
                        'point_repair_max_fraction',
                        Number(e.target.value),
                      )
                    }
                    inputProps={{ min: 0, max: 1, step: 0.05 }}
                    InputProps={{
                      endAdornment: (
                        <FieldInfo tip="Maximum repair fraction (0–1) for a pipe to be classified as needing only point repair. If repair fraction exceeds this, the pipe is escalated to section lining or higher." />
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    fullWidth
                    label="Section Lining Max"
                    type="number"
                    value={form.section_lining_max_fraction}
                    onChange={e =>
                      updateField(
                        'section_lining_max_fraction',
                        Number(e.target.value),
                      )
                    }
                    inputProps={{ min: 0, max: 1, step: 0.05 }}
                    InputProps={{
                      endAdornment: (
                        <FieldInfo tip="Maximum repair fraction (0–1) for CIPP section lining. Pipes with repair fraction above point repair max but below this are classified as needing section lining." />
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    fullWidth
                    label="Rehabilitation Max"
                    type="number"
                    value={form.rehabilitation_max_fraction}
                    onChange={e =>
                      updateField(
                        'rehabilitation_max_fraction',
                        Number(e.target.value),
                      )
                    }
                    inputProps={{ min: 0, max: 1, step: 0.05 }}
                    InputProps={{
                      endAdornment: (
                        <FieldInfo tip="Maximum repair fraction (0–1) for full rehabilitation. Pipes above this threshold are classified as needing replacement." />
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    fullWidth
                    label="Repair/Replace Threshold"
                    type="number"
                    value={form.repair_to_replace_threshold}
                    onChange={e =>
                      updateField(
                        'repair_to_replace_threshold',
                        Number(e.target.value),
                      )
                    }
                    inputProps={{ min: 0, max: 1, step: 0.05 }}
                    InputProps={{
                      endAdornment: (
                        <FieldInfo tip="If a partial repair (spot repair or section lining) cost exceeds this fraction of full replacement cost, the repair cost is capped at replacement cost. Prevents the optimizer from choosing expensive partial repairs over full replacement." />
                      ),
                    }}
                  />
                </Grid>
              </Grid>
            </>
          )}

          {/* GIS Enrichment */}
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            GIS Enrichment
          </Typography>

          {form.risk_config_id ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              CoF and LoF scores will be loaded from the selected Risk
              Configuration. GIS enrichment adds facility proximity scoring for
              assets with missing data.
            </Alert>
          ) : (
            !form.cof_estimation_enabled && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Attribute-based consequence estimation always runs for assets
                with missing data. Enable GIS enrichment below to also factor in
                proximity to hospitals, schools, and fire stations.
              </Alert>
            )
          )}

          <Box sx={{ mb: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={form.cof_estimation_enabled}
                  onChange={e =>
                    updateField('cof_estimation_enabled', e.target.checked)
                  }
                />
              }
              label="Enable GIS Enrichment"
            />
            <Tooltip
              title="When enabled, consequence scores are enriched with proximity to hospitals, schools, and fire stations. US locations use ESRI HIFLD data; international locations use OpenStreetMap. Attribute-based estimation (diameter, material, length) always runs regardless of this setting."
              arrow
              placement="right"
              componentsProps={INFO_TOOLTIP_PROPS}>
              <IconButton size="small" tabIndex={-1} sx={{ ml: 0.5 }}>
                <InfoIcon sx={{ fontSize: '1rem' }} />
              </IconButton>
            </Tooltip>
          </Box>

          {form.cof_estimation_enabled && (
            <>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    label="Default Consequence"
                    type="number"
                    value={form.default_consequence}
                    onChange={e =>
                      updateField('default_consequence', Number(e.target.value))
                    }
                    helperText="Fallback score when GIS lookup fails"
                    InputProps={{
                      endAdornment: (
                        <FieldInfo tip="Default consequence score assigned to assets when both GIS and proxy estimation fail. Higher values mean the optimizer treats unscored assets as more critical." />
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    label="GIS Weight"
                    type="number"
                    value={form.cof_gis_weight}
                    onChange={e =>
                      updateField('cof_gis_weight', Number(e.target.value))
                    }
                    inputProps={{ min: 0, max: 1, step: 0.1 }}
                    helperText="Blend: 0 = proxy only, 1 = GIS only"
                    InputProps={{
                      endAdornment: (
                        <FieldInfo tip="Blending weight between GIS-based and proxy-based consequence scores. 0 = use only asset attribute proxies, 1 = use only GIS facility proximity. Values in between blend both methods." />
                      ),
                    }}
                  />
                </Grid>
              </Grid>

              <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                Facility Search Configuration
              </Typography>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={3}>
                  <TextField
                    fullWidth
                    label="Buffer (meters)"
                    type="number"
                    value={form.cof_esri_buffer_meters}
                    onChange={e =>
                      updateField(
                        'cof_esri_buffer_meters',
                        Number(e.target.value),
                      )
                    }
                    inputProps={{ min: 0 }}
                    InputProps={{
                      endAdornment: (
                        <FieldInfo tip="Search radius around each asset for nearby critical facilities (hospitals, schools, fire stations). Larger buffers capture more facilities but may over-estimate consequence for isolated assets." />
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    fullWidth
                    label="Hospitals Weight"
                    type="number"
                    value={form.cof_esri_hospitals_weight}
                    onChange={e =>
                      updateField(
                        'cof_esri_hospitals_weight',
                        Number(e.target.value),
                      )
                    }
                    inputProps={{ min: 0, step: 0.5 }}
                    InputProps={{
                      endAdornment: (
                        <FieldInfo tip="Consequence multiplier for nearby hospitals. Higher values increase the consequence score for assets near hospitals, prioritizing their protection." />
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    fullWidth
                    label="Schools Weight"
                    type="number"
                    value={form.cof_esri_schools_weight}
                    onChange={e =>
                      updateField(
                        'cof_esri_schools_weight',
                        Number(e.target.value),
                      )
                    }
                    inputProps={{ min: 0, step: 0.5 }}
                    InputProps={{
                      endAdornment: (
                        <FieldInfo tip="Consequence multiplier for nearby schools. Higher values increase the consequence score for assets near schools." />
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    fullWidth
                    label="Fire Stations Weight"
                    type="number"
                    value={form.cof_esri_fire_stations_weight}
                    onChange={e =>
                      updateField(
                        'cof_esri_fire_stations_weight',
                        Number(e.target.value),
                      )
                    }
                    inputProps={{ min: 0, step: 0.5 }}
                    InputProps={{
                      endAdornment: (
                        <FieldInfo tip="Consequence multiplier for nearby fire stations. Higher values increase the consequence score for assets near fire stations." />
                      ),
                    }}
                  />
                </Grid>
              </Grid>

              <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                Proxy Weights
              </Typography>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={3}>
                  <TextField
                    fullWidth
                    label="Diameter"
                    type="number"
                    value={form.cof_proxy_diameter}
                    onChange={e =>
                      updateField('cof_proxy_diameter', Number(e.target.value))
                    }
                    inputProps={{ min: 0, max: 1, step: 0.05 }}
                    InputProps={{
                      endAdornment: (
                        <FieldInfo tip="Weight of pipe diameter in the proxy consequence score. Larger pipes serve more connections, so higher weight increases consequence for larger-diameter assets. Weights should sum to 1." />
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    fullWidth
                    label="Material"
                    type="number"
                    value={form.cof_proxy_material}
                    onChange={e =>
                      updateField('cof_proxy_material', Number(e.target.value))
                    }
                    inputProps={{ min: 0, max: 1, step: 0.05 }}
                    InputProps={{
                      endAdornment: (
                        <FieldInfo tip="Weight of pipe material in the proxy consequence score. Some materials (e.g. asbestos cement) pose higher consequence on failure. Weights should sum to 1." />
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    fullWidth
                    label="Subtype"
                    type="number"
                    value={form.cof_proxy_subtype}
                    onChange={e =>
                      updateField('cof_proxy_subtype', Number(e.target.value))
                    }
                    inputProps={{ min: 0, max: 1, step: 0.05 }}
                    InputProps={{
                      endAdornment: (
                        <FieldInfo tip="Weight of asset subtype (e.g. force main vs gravity) in the proxy consequence score. Different subtypes carry different failure impacts. Weights should sum to 1." />
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    fullWidth
                    label="Length"
                    type="number"
                    value={form.cof_proxy_length}
                    onChange={e =>
                      updateField('cof_proxy_length', Number(e.target.value))
                    }
                    inputProps={{ min: 0, max: 1, step: 0.05 }}
                    InputProps={{
                      endAdornment: (
                        <FieldInfo tip="Weight of pipe length in the proxy consequence score. Longer pipes affect more area on failure. Weights should sum to 1." />
                      ),
                    }}
                  />
                </Grid>
              </Grid>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Critical Asset IDs (comma-separated)"
                    value={form.cof_critical_assets}
                    onChange={e =>
                      updateField('cof_critical_assets', e.target.value)
                    }
                    helperText="Assets that always receive maximum consequence scores"
                    InputProps={{
                      endAdornment: (
                        <FieldInfo tip="Comma-separated list of asset IDs that should always receive the highest consequence score, regardless of GIS or proxy estimation. Use for assets you know are critical (e.g. hospital feed mains)." />
                      ),
                    }}
                  />
                </Grid>
              </Grid>
            </>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Validation / Errors */}
      {validationResult && (
        <Box sx={{ mb: 2 }}>
          {validationResult.valid && (
            <Alert severity="success" icon={<CheckCircleIcon />}>
              Configuration is valid
            </Alert>
          )}
          {validationResult.errors.map((e, i) => (
            <Alert key={i} severity="error" sx={{ mt: 1 }}>
              {e}
            </Alert>
          ))}
          {validationResult.warnings.map((w, i) => (
            <Alert key={i} severity="warning" sx={{ mt: 1 }}>
              {w}
            </Alert>
          ))}
        </Box>
      )}
      {submitError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {submitError}
        </Alert>
      )}

      {/* Actions */}
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button
          variant="outlined"
          onClick={handleValidate}
          disabled={submitting}>
          Validate
        </Button>
        <Button
          variant="contained"
          startIcon={
            submitting ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              <PlayArrowIcon />
            )
          }
          onClick={handleSubmit}
          disabled={submitting}>
          {submitting ? 'Running...' : 'Launch Run'}
        </Button>
      </Box>
    </Box>
  );
}

export default RunConfigPage;
